import { EyeOutlined } from '@ant-design/icons';
import {
  Card,
  Form,
  Grid,
  Image,
  message,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { Modal } from 'components';
import { useUploadImage } from 'features/Dashboard/use-cases/upload-image';
import { useUploadDocuments } from 'features/document-report/use-cases/upload-documents';
import { useUserContext } from 'features/users';
import { StyledUserModalWrapper } from 'features/users/components/modals/user-modal.styles';
import { Folder } from 'lucide-react';
import { useState } from 'react';
import { convertFileSize, validateFileBeforeUpload } from 'routes/utils';

const { useBreakpoint } = Grid;

export const UploadRequestedDocumentsModal = ({
  onClose,
  intialFetch,
  documentInfo,
  month,
  year,
  getDocumentsCount,
  handleClick,
}: {
  onClose: () => void;
  intialFetch: any;
  documentInfo: any;
  month: any;
  year: any;
  getDocumentsCount: any;
  handleClick: any;
}) => {
  const { user } = useUserContext();
  const formName = 'add-user-form';
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const { isLoading, mutate } = useUploadDocuments();
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [isImageUploading, setIsImageUploading] = useState<boolean>(false); // Loading state for image upload
  const { isLoading: imageLoading, mutate: uploadImage } = useUploadImage();
  const [blobUrl, setBlobUrl] = useState<string>(''); // Store the uploaded blob URL

  const handleImageUpload = (file: UploadFile) => {
    const formData = new FormData();
    formData.append('file', file as any);

    setIsImageUploading(true); // Set uploading state to true
    uploadImage(
      { id: user?.CompanyId as string, formData }, // Pass id and file as payload
      {
        onSuccess: (response: any) => {
          setFileNames((prev: any) => [...prev, response.fileName]);
          setBlobUrl(response.blobUrl);
          message.success('File uploaded successfully');
          setIsImageUploading(false); // Reset uploading state
        },
        onError: () => {
          message.error('Image upload failed');
          setIsImageUploading(false); // Reset uploading state
        },
      },
    );
  };

  const handleUpdate = () => {
    const IsClientUpload = true;
    // const formData = new FormData();

    if (!fileList || fileList.length === 0) {
      message.error('Please upload at least one file.');

      return;
    }
    // Collect files and their blob URLs
    const files = fileList
      .map((fileItem, index) => {
        const file = fileItem.originFileObj;
        const fileSize = file.size;
        const documentName = documentInfo?.documentName;
        if (!file) return null;
        const fileData = {
          documentName,
          fileName: fileNames[index] || '',
          fileSize: convertFileSize(fileSize),
          // blobUrl: blobUrl,  // Add blobUrl for each file
        };

        return fileData;
      })
      .filter((file) => file !== null);

    const payload = {
      companyId: user?.CompanyId,
      documentId: documentInfo?.documentId,
      serviceId: documentInfo?.serviceId,
      isClientUpload: IsClientUpload,
      files: files, // Send files with their blob URLs
      year: +year,
      month: +month,
    };

    mutate(payload, {
      onSuccess: () => {
        onClose();
        intialFetch();
        getDocumentsCount();
        handleClick('Requested');
      },
    });
  };
  const beforeUpload = (file: any) => {
    const isValid = validateFileBeforeUpload(file);
    if (isValid) {
      handleImageUpload(file); // Call handleImageUpload only if valid
    }

    return isValid; // If false, upload will be prevented
  };
  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  return (
    <StyledUserModalWrapper>
      <Modal
        open
        centered
        title={'Upload requested document'}
        onCancel={onClose}
        getContainer={false}
        onOk={form.submit}
        okText={'Upload'}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={screens.lg ? '520px' : '100%'}
        okButtonProps={{
          form: formName,
          loading: isLoading,
          disabled: imageLoading || isImageUploading,
        }}
        maskClosable={false}
      >
        <Form
          id={formName}
          name={formName}
          form={form}
          layout='vertical'
          onFinish={handleUpdate}
          // validateTrigger={validateTrigger}
          disabled={isLoading}
        >
          <Card className='mb-4 bg-gray-50 cursor-pointer hover:bg-gray-100'>
            <div className='flex gap-2'>
              <Folder fill='#aaa' stroke='none' size={62} />
              <div className='flex flex-col grow'>
                <p>{documentInfo?.documentName}</p>
                <div className='mb-4'>
                  {documentInfo?.categoryName && (
                    <span className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded leading-5 mr-2'>
                      {documentInfo?.categoryName}
                    </span>
                  )}
                  {documentInfo?.subCategoryName && (
                    <span className='bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded leading-5'>
                      {documentInfo?.subCategoryName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
          <Form.Item label={'Upload Document'} name={'file'}>
            <div className='upld_item'>
              <div className='updcbx'>
                <Upload
                  action='https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload'
                  listType='picture-card'
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  maxCount={1}
                  style={{ width: '100%' }}
                  beforeUpload={beforeUpload}
                  className='upld_docs'
                >
                  {fileList?.length >= 1 ? null : (
                    <div className='upld_img'>
                      <img src='/upload-img.png' alt='logo' className='' />
                      <p className='drp_text'>
                        Drag and drop here <span>or</span>{' '}
                        <a href='javascript:void(0)'>Browse Files</a>
                      </p>
                      {/* {uploadButton} */}
                    </div>
                  )}
                </Upload>
                {blobUrl && fileList.length > 0 && (
                  <EyeOutlined
                    className='eyeiconi'
                    onClick={() => setPreviewOpen(true)}
                  />
                )}
              </div>
              {isImageUploading && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                  }}
                >
                  <Spin size='large' />
                </div>
              )}
            </div>
            <Modal
              open={previewOpen}
              onCancel={() => setPreviewOpen(false)}
              footer={null}
              centered
              width={800}
            >
              {blobUrl.endsWith('.jpg') ||
              blobUrl.endsWith('.jpeg') ||
              blobUrl.endsWith('.png') ||
              blobUrl.endsWith('.webp') ? (
                <Image src={blobUrl} alt='Preview' />
              ) : (
                <iframe
                  src={`https://docs.google.com/gview?url=${blobUrl}&embedded=true`}
                  style={{ width: '100%', height: '600px', border: 'none' }}
                  title='Document Preview'
                />
              )}
            </Modal>
            {previewImage && (
              <Image
                wrapperStyle={{ display: 'none' }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(''),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>
          {/* <Form.Item
            label='Notes'
            name='DocumentName'
            rules={[
              {
                required: true,
                message: 'Enter Notes',
              },
            ]}
          >
            <Input placeholder='Notes' />
          </Form.Item> */}
        </Form>
      </Modal>
    </StyledUserModalWrapper>
  );
};
